import { CommonModule } from '@angular/common';
import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { finalize, map, Subscription, take } from 'rxjs';
import { collapseAnimation } from '../../animations/collapse.animation';
import { HourRateFgComponent, IHourRateFG, getHourRateFG } from '../../components/hour-rate-fg/hour-rate-fg.component';
import { RoleType } from '../../enums/user/role-types.enum';
import { Operation } from '../../enums/utils/operations.enum';
import { IAddOrgMemberData } from '../../models/organization/members/add-org-member-data.model';
import { IInviteUnregisteredMemberData } from '../../models/organization/members/invite-unregistered-member-data.model';
import { IUpdateOrgMemberData } from '../../models/organization/members/update-org-member-data.model';
import { IOrgMemberCustomData, IUser, IUserRole } from '../../models/user/user.model';
import { IOrgLecturerSession, OrgMemberCustomDataResultType, SessionState, UsersService } from '../../services/entities/users/users.service';
import { SelectedOrgService } from '../../services/selected-org.service';
import { IOrgUsersState, OrgUsersStoreService } from '../../services/store/org-users-store.service';
import { StoreService } from '../../services/store/store.service';
import { IUserState, UserStoreService } from '../../services/store/user-store.service';
import { UtilsService } from '../../services/utils.service';
import { ModalHeaderComponent } from '../components/modal-header/modal-header.component';
import { Modal } from '../modal';
import { InputSwitchModule } from 'primeng/inputswitch';
import { LocalizedDatePipe } from '../../pipes/localized-date.pipe';
import { ImageInputComponent } from '../../components/image-input/image-input.component';
import { ImageSize } from '../../enums/image/image-sizes.enum';
import { EditorModule } from 'primeng/editor';
import { emailValidator } from '../../utils/session-group-form-utils/validators';
import { TranslateModule } from '@ngx-translate/core';
import { ILocket } from '../../models/locket/locket.model';
import { ActionBy } from '../../enums/utils/action-by.enum';
import { OrganizationsService } from '../../services/entities/organizations/organizations.service';
import { CustomOrgMemberInputComponent } from './custom-org-member-input/custom-org-member-input.component';
import { ModalService } from '../../services/modal.service';
import { Color } from '../../enums/utils/colors';
import { NavigationService } from '../../services/navigation.service';
import { SessionCardModalComponent } from 'src/app/pages/admin/org-admin/components/modals/session-card-modal/session-card-modal.component';
import { UserHelperService } from '../../services/helpers/user-helper.service';
import { DataType } from '../../enums/collecting-data/data-type.enum';
import { getTitlesInputFG, ITitlesFG } from 'src/app/pages/admin/components/custom-data-inputs/titles-input/titles-input.component';
import { GoogleCalendarSyncComponent } from '../../components/google-calendar-sync/google-calendar-sync.component';


interface IAddOrgUserForm {
  roles: FormControl<RoleType[]>;
  email: FormControl<string | null>;
  name: FormControl<string>;
  surname: FormControl<string>;
  sendInvitation?: FormControl<boolean>;

  lecturerNote?: FormControl<string | null>;
  lecturerHourRate?: FormGroup<IHourRateFG>;

  profilePictureBase64: FormControl<string | null>;
  bio: FormControl<string | null>;
  nickname: FormControl<string | null>;

  orgMemberData: FormGroup<IOrgMemberDataFG>;
};

interface IOrgMemberDataFG {
  [uuid: string]: FormControl<any> | FormGroup<any>;
}

export interface IAddOrgUserInitValues {
  name?: string,
  surname?: string
}

interface IOrgLecturerSessionExt extends IOrgLecturerSession {
  stateColor: string;
}

const SESSIONS_SIZE = 10;

@Component({
  selector: 'app-org-member-modal',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    CheckboxModule, ButtonModule, MessageModule, InputTextModule, InputSwitchModule, EditorModule,
    ModalHeaderComponent, HourRateFgComponent, LocalizedDatePipe, ImageInputComponent, TranslateModule, CustomOrgMemberInputComponent,
    GoogleCalendarSyncComponent
  ],
  templateUrl: './org-member-modal.component.html',
  styleUrls: ['./org-member-modal.component.scss'],
  animations: [ collapseAnimation ]
})
export class OrgMemberModalComponent extends Modal implements OnInit, OnDestroy  {
  Operation = Operation;
  RoleType = RoleType;

  @Input({required: true}) operation: Operation | undefined;
  @Input({required: true}) toOrganizationId: number | undefined;
  @Input() userId: number | undefined;
  @Input() preselectedRoles: RoleType[] = [];
  @Input() initValues: IAddOrgUserInitValues = {};

  user: IUser & { roles: IUserRole[], lockets: ILocket[] } | null = null;
  fetchingUser: boolean = false;
  customMemberFormSettingsWithData: OrgMemberCustomDataResultType[] = [];
  fetchingOrgMemberData: boolean = false;
  fetchingOrgLecturerSessions: boolean = false;
  orgLecturerSessions: IOrgLecturerSessionExt[] = [];
  fetchingNextOrgLecturerSessions: boolean = false;
  showFetchNextSessions: boolean = false;

  currentSessionsPage = 1;

  @HostBinding('class.loading-box') get fetching() { return this.fetchingUser || this.fetchingOrgMemberData || this.fetchingOrgLecturerSessions; };

  orgUsersState: IOrgUsersState | undefined;
  userState: IUserState | undefined;

  form = new FormGroup<IAddOrgUserForm>({
    roles: new FormControl([], { nonNullable: true, validators: [ Validators.required ]}),
    email: new FormControl(null, { validators: [ emailValidator, this.customEmailValidator.bind(this) ]}),
    name: new FormControl('', { nonNullable: true, validators: [ Validators.required ]}),
    surname: new FormControl('', { nonNullable: true, validators: [ Validators.required ]}),

    // hourRate is dynamic based on presence of lecturer role
    // lecturerNote is dynamic based on presence of lecturer role
    // sendInvitation is dynamic based on Operation.CREATE

    profilePictureBase64: new FormControl(null),
    bio: new FormControl(null),
    nickname: new FormControl(null),

    orgMemberData: new FormGroup<IOrgMemberDataFG>({})
  });

  possibleRoles: RoleType[] = [ RoleType.LECTURER, RoleType.COORDINATOR, RoleType.ORG_ADMIN ];

  profilePictureChanged = false;
  profilePicturePreview: any;
  sending: boolean = false;
  subs: Subscription[] = [];
  CANNOT_UPDATE_EMAIL_ALREADY_EXIST = false;
  lecturerSectionCollapsed = true;
  lecturerSessionsCollapsed = true;

  constructor(
    private utilsService: UtilsService,
    private orgUsersStore: OrgUsersStoreService,
    private userStore: UserStoreService,
    private store: StoreService,
    private usersService: UsersService,
    private selectedOrgService: SelectedOrgService,
    private orgsService: OrganizationsService,
    private modalService: ModalService,
    private navService: NavigationService,
    private userHelperService: UserHelperService
  ) {
    super();
  }


  override ngOnInit(): void {
    super.ngOnInit();

    this.orgUsersState = this.orgUsersStore.getState();
    this.userState = this.userStore.state;


    if (this.operation === Operation.CREATE) {
      const FC = new FormControl(false, { nonNullable: true });
      this.form.addControl('sendInvitation', FC);

      this.watchForm();

      this.form.patchValue({ roles: this.preselectedRoles, ...this.initValues });
    }
    else if (this.operation === Operation.UPDATE && this.userId) {
      this.initUpdate();
    }

    this.fetchOrgMemberData();

  }

  private initUpdate() {
    if (!this.userId) return;

    // fetch user and then init form etc..
    this.fetchingUser = true;
    this.usersService.getUserById(this.userId, ActionBy.ADMIN, this.toOrganizationId).pipe(
      take(1),
    ).subscribe({
      next: (res) => {
        this.fetchingUser = false;
        this.user = res;

        this.watchForm();
        if (this.user) {
          // nedovolit upravovat email pokud uz user nejaky ma (povolit pokud nema)
          if (this.user.email) {
            this.emailFC?.disable();
          }
          const roleTypes = this.user.roles.map((role) => role.type);
          if (
            this.user.registered &&
            (
              !roleTypes.includes(RoleType.LECTURER) ||
              [RoleType.ORG_ADMIN, RoleType.ORG_OWNER, RoleType.COORDINATOR, RoleType.SYSTEM_ADMIN].some((roleType) => roleTypes.includes(roleType)) ||
              !this.userStore.hasAnyUserRole([RoleType.ORG_ADMIN, RoleType.ORG_OWNER, RoleType.COORDINATOR, RoleType.SYSTEM_ADMIN], this.selectedOrgService.getCurrentValue())
            )
          ) {
            this.nameFC?.disable();
            this.surnameFC?.disable();
          }
          this.patchForm(this.user);
        }
      },
      error: (err) => {
        this.fetchingUser = false;
      }
    });
  }

  patchForm(user: IUser) {
    const rolesInOrg = user.roles.filter(x => x.organizationId === this.toOrganizationId);
    const locketInOrg = user.lockets?.find(x => x.organizationId === this.toOrganizationId);
    const lecturerRole = rolesInOrg.find(x => x.type === RoleType.LECTURER);

    this.form.patchValue({
      email: user.email,
      name: this.userHelperService.getOrgUserName(user.orgUsers, this.toOrganizationId || null, true),
      surname: this.userHelperService.getOrgUserSurname(user.orgUsers, this.toOrganizationId || null, true),
      roles: rolesInOrg.map(x => x.type),
      lecturerHourRate: lecturerRole?.hourRate ?? undefined,
      lecturerNote: lecturerRole?.adminNotes?.[0]?.text ?? undefined,

      bio: locketInOrg?.bio ?? null,
      nickname: locketInOrg?.nickname ?? null,
    });
    this.profilePicturePreview = locketInOrg?.imageUrls?.[ImageSize._128x128] ?? user.photoUrls?.[ImageSize._128x128];
  }

  onSubmit(createAnother: boolean = false) {
    this.utilsService.markFormGroupDirty(this.form);

    if (this.form.invalid || this.sending || !this.toOrganizationId) {
      console.error('invalid');
      return;
    }

    switch (this.operation) {
      case Operation.CREATE:
        this.create(createAnother);
        break;
      case Operation.UPDATE:
        this.update();
        break;
      default:
        this.utilsService.logError('Should not happen! po23j09ewusd-0poe');
        break;
    }
  }

  private create(createAnother: boolean = false) {
    if (!this.toOrganizationId) return;
    const user = this.userStore.state.user$.getValue();
    if (!user) return;

    const formVal = this.form.getRawValue();
    const data: IAddOrgMemberData = {
      roles: formVal.roles,
      email: formVal.email,
      name: formVal.name,
      surname: formVal.surname,
      sendInvitation: formVal.sendInvitation!,
      lecturerHourRate: formVal.lecturerHourRate,
      lecturerNote: formVal.lecturerNote ?? null,
      preferredLanguage: user.preferredLanguage,

      orgId: this.toOrganizationId,

      imgBase64: this.profilePictureChanged ? (formVal.profilePictureBase64 ?? undefined) : undefined,
      bio: formVal.bio,
      nickname: formVal.nickname,

      orgMemberData: formVal.orgMemberData
    };

    this.sending = true;
    this.orgUsersStore.addOrgMember(data).pipe(
      finalize(() => this.sending = false)
    ).subscribe({
      next: (res) => {
        if (createAnother) {
          if (!this.toOrganizationId) return;
          this.modalService.openOrgMemberModal({
            c: OrgMemberModalComponent,
            operation: Operation.CREATE,
            toOrganizationId: this.toOrganizationId,
            initValues: this.initValues,
            preselectedRoles: this.preselectedRoles,
          })
        }
        this.close();
        
      }
    });
  }

  private update() {
    if (!this.toOrganizationId || !this.user) return;
    const formVal = this.form.getRawValue();
    const data: IUpdateOrgMemberData = {
      userId: this.user.id,
      email: this.user.email ? undefined : (formVal.email ?? undefined), // poslat pouze pokud user nema email. Je osetrene i na BE
      roles: formVal.roles,
      name: formVal.name,
      surname: formVal.surname,
      orgId: this.toOrganizationId,
      lecturerHourRate: formVal.lecturerHourRate,
      lecturerNote: formVal.lecturerNote ?? null,

      imgBase64: this.profilePictureChanged ? (formVal.profilePictureBase64 ?? undefined) : undefined,
      bio: formVal.bio,
      nickname: formVal.nickname,

      orgMemberData: formVal.orgMemberData
    };

    this.sending = true;
    this.orgUsersStore.updateOrgMember(data).pipe(
      finalize(() => this.sending = false)
    ).subscribe({
      next: (res) => {
        this.close();
      },
      error: (err) => {
        if (err.details?.CANNOT_UPDATE_EMAIL_ALREADY_EXIST) {
          this.CANNOT_UPDATE_EMAIL_ALREADY_EXIST = true;
        }
      }
    });
  }

  pictureChanged(imageBase64: string) {
    this.profilePictureChanged = true;
    this.profilePictureBase64FC?.setValue(imageBase64)
    this.profilePicturePreview = imageBase64;
  }

  onInvite() {
    if (!this.user || !this.toOrganizationId || !this.user.email) return;
    const data: IInviteUnregisteredMemberData = {
      userId: this.user.id,
      orgId: this.toOrganizationId
    };
    this.store.actions.orgUsers_inviteUnregistered$.next(data);
  }

  watchForm() {
    this.watchRoles();
    this.watchSendInvitation();
  }
  watchRoles() {
    // if lecturer selected add hourRate FG
    this.subs.push(
      this.rolesFC.valueChanges.subscribe(roles => {
        if (roles.includes(RoleType.LECTURER)) {
          this.addHourRateFG();
          this.addLecturerNoteFC();
        } else {
          this.removeHourRateFG();
          this.removeLeacturerNoteFC();
        }

        if (this.customMemberFormSettingsWithData) this.initCustomMemberFormSettingsFG(this.customMemberFormSettingsWithData);
      })
    );
  }
  watchSendInvitation() {
    this.subs.push(
      this.sendInvitationFC?.valueChanges.subscribe(bool => {
        if (bool) {
          this.emailFC?.addValidators([Validators.required]);
        } else {
          this.emailFC?.removeValidators([Validators.required]);
        }
        this.emailFC?.updateValueAndValidity();
      }) ?? Subscription.EMPTY
    )
  }

  onShopItemTitle(session: IOrgLecturerSession) {
    this.navService.goToAdminShopItem({
      eventUuid: session.eventUuid,
      shopItemUuid: session.shopItemUuid,
      newTab: true
    });
  }

  onSession(session: IOrgLecturerSession) {
    this.modalService.openSessionCardModal({
      c: SessionCardModalComponent,
      sessionUuid: session.uuid
    });
  }

  private fetchOrgMemberData() {
    if (!this.toOrganizationId) return;

    this.fetchingOrgMemberData = true;
    this.usersService.getOrgMemberData({
      orgId: this.toOrganizationId,
      userId: this.userId ?? null
    }).pipe(
      take(1)
    ).subscribe({
      next: (res) => {
        this.customMemberFormSettingsWithData = res;
        this.fetchingOrgMemberData = false;
        this.initCustomMemberFormSettingsFG(res);
      },
      error: () => this.fetchingOrgMemberData = false
    });

    if (this.userId) {
      this.fetchingOrgLecturerSessions = true;
      this.usersService.getOrgLecturerSessions({
        orgId: this.toOrganizationId,
        userId: this.userId,
        page: this.currentSessionsPage,
        size: SESSIONS_SIZE
      }).pipe(
        take(1)
      ).subscribe({
        next: (res) => {
          this.orgLecturerSessions = res.data.map((r) => ({ ...r, stateColor: this.getSessionStateColor(r.state) }));
          this.showFetchNextSessions = res.next;
          this.fetchingOrgLecturerSessions = false;
        },
        error: () => this.fetchingOrgLecturerSessions = false
      });
    }
  }

  private getSessionStateColor(sessionState: SessionState) {
    const colorMap = new Map<SessionState, string>([
      [SessionState.CANCELLED, Color.UNKNOWN],
      [SessionState.FINISHED, Color.SUCCESS],
      [SessionState.MISSING_LECTURER_ATTENDANCE, Color.DANGER],
      [SessionState.PLANNED, Color.INFO]
    ]);
    return colorMap.get(sessionState) || '';
  }

  onLoadNextSessions() {
    if (!this.toOrganizationId) return;

    this.fetchingNextOrgLecturerSessions = true;
    this.usersService.getOrgLecturerSessions({
      orgId: this.toOrganizationId,
      userId: this.userId!,
      page: ++this.currentSessionsPage,
      size: SESSIONS_SIZE
    }).pipe(
      take(1)
    ).subscribe({
      next: (res) => {
        this.orgLecturerSessions = [...this.orgLecturerSessions, ...res.data.map((r) => ({ ...r, stateColor: this.getSessionStateColor(r.state) }))];
        this.showFetchNextSessions = res.next;
        this.fetchingNextOrgLecturerSessions = false;
      },
      error: () => this.fetchingNextOrgLecturerSessions = false
    });
  }

  private initCustomMemberFormSettingsFG(settingsWithData: OrgMemberCustomDataResultType[]) {
    const FG = this.form.controls.orgMemberData;

    // remove all controls of FG
    Object.keys(FG.controls).forEach(key => {
      if (FG.get(key)) (<any>FG).removeControl(<any>key);
    });

    settingsWithData.forEach(x => {
      let value = x.value;

      let FC: FormControl<any> | FormGroup<any> = new FormControl(value, { nonNullable: true });
      if (x.dataType === DataType.TITLES) {
        FC = getTitlesInputFG();
      }
      const selectedRoles = this.form.controls.roles.value;

      if (selectedRoles.includes(x.userRoleType)) {
        FG.addControl(x.uuid, FC);
      }
    });
  }

  private addLecturerNoteFC() {
    if (!this.form.controls.lecturerNote) {
      this.form.addControl('lecturerNote', new FormControl(null));
    }
  }
  private removeLeacturerNoteFC() {
    if (this.form.controls.lecturerNote) {
      this.form.removeControl('lecturerNote');
    }
  }
  private addHourRateFG() {
    if (!this.lecturerHourRateFG) {
      this.form.addControl('lecturerHourRate', getHourRateFG());
    }
  }
  private removeHourRateFG() {
    if (this.lecturerHourRateFG) {
      this.form.removeControl('lecturerHourRate');
    }
  }

  private customEmailValidator() {
    const sendInvitation = this.sendInvitationFC?.value;
    if (sendInvitation) {
      if (!this.emailFC?.value?.length) return { required: true };
    }
    return null;
  }

  get hasAnyNotes() {
    return this.user?.roles.some(x => x.adminNotes?.length) ?? false;
  }

  get showAdminOption$() {
    return this.userState?.user$.pipe(
      map(x => {
        return this.usersService.hasAnyRolesOfOrg(x?.roles ?? [], [ RoleType.ORG_OWNER, RoleType.ORG_ADMIN ], this.selectedOrgService.getCurrentValue()) || this.usersService.isRoleOfOrg(x?.roles??[], RoleType.SYSTEM_ADMIN, null)
      })
    );
  }

  get showCoordinatorOption$() {
    return this.userState?.user$.pipe(
      map(x => {
        return this.usersService.hasAnyRolesOfOrg(x?.roles ?? [], [ RoleType.ORG_OWNER, RoleType.ORG_ADMIN ], this.selectedOrgService.getCurrentValue()) || this.usersService.isRoleOfOrg(x?.roles??[], RoleType.SYSTEM_ADMIN, null)
      })
    );
  }

  get nameFC() {
    return this.form.get('name');
  }
  get surnameFC() {
    return this.form.get('surname');
  }
  get rolesFC() {
    return this.form.get('roles') as FormControl<RoleType[]>;
  }
  get lecturerHourRateFG() {
    return this.form.get('lecturerHourRate') as FormGroup<IHourRateFG> | undefined;
  }
  get sendInvitationFC() {
    return this.form?.controls['sendInvitation'];
  }
  get emailFC() {
    return this.form.get('email');
  }
  get profilePictureBase64FC() {
    return this.form.controls.profilePictureBase64;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    
    this.subs.forEach(s => s.unsubscribe());
  }
}
